export const ASSET_SCHEMA_VERSION = 1 as const;

export type SupportedAssetMimeType =
  | 'image/png'
  | 'image/jpeg'
  | 'image/webp'
  | 'image/svg+xml'
  | 'application/pdf';

export type AssetKind = 'raster-image' | 'svg' | 'pdf';

export interface AssetLimits {
  readonly maxFileBytes: number;
  readonly maxRasterPixels: number;
  readonly maxRasterDimensionPx: number;
  readonly maxSvgBytes: number;
  readonly maxPdfPages: number;
}

export const DEFAULT_ASSET_LIMITS: AssetLimits = {
  maxFileBytes: 15 * 1024 * 1024,
  maxRasterPixels: 40_000_000,
  maxRasterDimensionPx: 16_384,
  maxSvgBytes: 2 * 1024 * 1024,
  maxPdfPages: 20,
};

export interface RasterAssetMetadata {
  readonly kind: 'raster-image';
  readonly widthPx: number;
  readonly heightPx: number;
  readonly pixelCount: number;
  readonly exifOrientation: number | null;
}

export interface SvgAssetMetadata {
  readonly kind: 'svg';
  readonly widthPx: number;
  readonly heightPx: number;
  readonly viewBox: readonly [number, number, number, number] | null;
  /** El contenido no se modifica; solo supero controles conservadores de contenido activo. */
  readonly activeContentChecksPassed: true;
}

export interface PdfPageMetadata {
  readonly pageIndex: number;
  readonly widthPt: number;
  readonly heightPt: number;
  readonly rotationDeg: number;
}

export interface PdfDocumentMetadata {
  readonly title: string | null;
  readonly author: string | null;
  readonly subject: string | null;
  readonly creator: string | null;
  readonly producer: string | null;
  readonly creationDateIso: string | null;
  readonly modificationDateIso: string | null;
}

export interface PdfAssetMetadata {
  readonly kind: 'pdf';
  readonly pageCount: number;
  readonly selectedPageIndex: number;
  readonly pages: readonly PdfPageMetadata[];
  readonly document: PdfDocumentMetadata;
  /** Los bytes originales se conservan en el repositorio, nunca dentro del descriptor. */
  readonly originalBytesPreserved: true;
}

export type AssetMetadata =
  | RasterAssetMetadata
  | SvgAssetMetadata
  | PdfAssetMetadata;

export interface AssetRecordV1 {
  readonly schemaVersion: typeof ASSET_SCHEMA_VERSION;
  readonly assetId: string;
  readonly contentHash: string;
  readonly originalFileName: string;
  readonly displayName: string;
  readonly mimeType: SupportedAssetMimeType;
  readonly sizeBytes: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly metadata: AssetMetadata;
}

export type AssetBinaryInput = Uint8Array | ArrayBuffer | Blob;

export interface ImportAssetInput {
  readonly bytes: AssetBinaryInput;
  readonly fileName: string;
  readonly declaredMimeType?: string | null;
  readonly selectedPdfPageIndex?: number;
  readonly now?: string;
}

export interface UpdateAssetInput {
  readonly displayName: string;
  readonly now?: string;
}

export interface AssetObjectUrlHandle {
  readonly assetId: string;
  readonly url: string;
  readonly revoked: boolean;
  revoke(): void;
}

export interface AssetRepository {
  import(input: ImportAssetInput): Promise<AssetRecordV1>;
  get(assetId: string): Promise<AssetRecordV1 | null>;
  getBytes(assetId: string): Promise<Uint8Array | null>;
  list(): Promise<readonly AssetRecordV1[]>;
  update(assetId: string, input: UpdateAssetInput): Promise<AssetRecordV1>;
  selectPdfPage(assetId: string, pageIndex: number, now?: string): Promise<AssetRecordV1>;
  delete(assetId: string): Promise<boolean>;
  createObjectUrl(assetId: string): Promise<AssetObjectUrlHandle>;
  dispose(): void;
}

export interface AssetPreflightResult {
  readonly record: AssetRecordV1;
  readonly bytes: Uint8Array;
}
